import React, { useState, useEffect, useCallback } from 'react';
import './Reviews.css';
import { reviewsData } from '../../utils/reviewsData';
import { FaStar, FaQuoteLeft, FaQuoteRight, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import Lottie from 'lottie-react';

const Reviews = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  
  const goToNext = useCallback(() => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % reviewsData.length);
    
    const timer = setTimeout(() => {
      setIsAnimating(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [isAnimating, reviewsData.length]);
  
  const goToPrev = () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? reviewsData.length - 1 : prevIndex - 1
    );
    
    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };
  
  // Auto-cycle through reviews
  useEffect(() => {
    const interval = setInterval(() => {
      goToNext();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [goToNext]);  // Added goToNext to dependency array
  
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<FaStar key={i} className="star filled" />);
    }
    
    if (hasHalfStar) {
      stars.push(<FaStar key="half" className="star half-filled" />);
    }
    
    const emptyStars = 5 - stars.length;
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<FaStar key={`empty-${i}`} className="star" />);
    }
    
    return stars;
  };
  
  return (
    <section id="reviews" className="reviews-section">
      <div className="container">
        <div className="section-header">
          <h2>Student Reviews</h2>
          <p>What our students say about their learning journey</p>
        </div>
        
        <div className="reviews-container">
          <div className="animation-container">
            <Lottie 
              path="/assets/lottie/second_animation.json"
              loop={true}
              className="lottie-animation"
            />
          </div>
          
          <div className="testimonials-container">
            <div className="testimonial-card">
              <div className={`testimonial-content ${isAnimating ? 'animating' : ''}`}>
                <div className="quote-icon">
                  <FaQuoteLeft />
                </div>
                <p className="testimonial-text">{reviewsData[currentIndex].review}</p>
                <div className="quote-icon right">
                  <FaQuoteRight />
                </div>
                <div className="testimonial-meta">
                  <div className="stars-container">
                    {renderStars(reviewsData[currentIndex].rating)}
                  </div>
                  <h4 className="testimonial-name">{reviewsData[currentIndex].name}</h4>
                  <p className="testimonial-course">{reviewsData[currentIndex].course}</p>
                </div>
              </div>
            </div>
            
            <div className="navigation-buttons">
              <button 
                className="nav-btn prev" 
                onClick={goToPrev}
                aria-label="Previous review"
              >
                <FaChevronLeft />
              </button>
              <div className="pagination-dots">
                {reviewsData.map((_, index) => (
                  <span 
                    key={index} 
                    className={`dot ${index === currentIndex ? 'active' : ''}`}
                    onClick={() => {
                      if (isAnimating) return;
                      setIsAnimating(true);
                      setCurrentIndex(index);
                      setTimeout(() => {
                        setIsAnimating(false);
                      }, 500);
                    }}
                  ></span>
                ))}
              </div>
              <button 
                className="nav-btn next" 
                onClick={goToNext}
                aria-label="Next review"
              >
                <FaChevronRight />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
